import React, { useState } from 'react';
import { useForm, SubmitHandler } from 'react-hook-form';
import { useAuth } from '../../contexts/AuthContext';
import { LoginCredentials } from '../../types';
import { Eye, EyeOff, LogIn, AlertCircle } from 'lucide-react';

const Login: React.FC = () => {
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');

  const { login } = useAuth();
  const { register, handleSubmit, formState: { errors } } = useForm<LoginCredentials>();

  const onSubmit: SubmitHandler<LoginCredentials> = async (data) => {
    setIsLoading(true);
    setError('');
    try {
      await login(data);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Identifiants incorrects. Veuillez réessayer.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex" style={{ background: 'var(--page-bg)' }}>

      {/* ── Left panel — brand / decorative ── */}
      <div
        className="hidden lg:flex lg:w-5/12 xl:w-1/2 flex-col justify-between p-12 relative overflow-hidden"
        style={{ background: 'var(--grad-sidebar)' }}
      >
        {/* Decorative circles */}
        <div
          className="absolute -top-24 -left-24 w-80 h-80 rounded-full opacity-10"
          style={{ background: 'var(--grad-blue)' }}
        />
        <div
          className="absolute bottom-0 -right-16 w-96 h-96 rounded-full opacity-10"
          style={{ background: 'var(--grad-teal)' }}
        />
        <div
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-64 h-64 rounded-full opacity-5"
          style={{ background: 'radial-gradient(circle, #2BAA8C, transparent)' }}
        />

        {/* Logo */}
        <div className="relative z-10">
          <div className="inline-block bg-white rounded-2xl px-7 py-4 shadow-lg">
            <img
              src="/Logo - SONRISA.png"
              alt="Cabinet Dentaire Sonrisa"
              className="h-18 w-auto"
              style={{ objectFit: 'contain' }}
            />
          </div>
        </div>

        {/* Center content */}
        <div className="relative z-10 space-y-6">
          {/* Tooth decorative SVG */}
          <div
            className="w-20 h-20 rounded-2xl flex items-center justify-center mb-8"
            style={{ background: 'rgba(43,170,140,0.18)', border: '1px solid rgba(43,170,140,0.25)' }}
          >
            <svg viewBox="0 0 64 64" className="w-10 h-10 fill-current" style={{ color: 'var(--teal-400)' }}>
              <path d="M32 6C21.5 6 14 14 14 22c0 4.5 1.2 8 2.8 11.5L20 52c.5 2 2 3 3.5 3 2 0 3.5-1.5 3.5-3.5V44c0-1.7 1.3-3 3-3h4c1.7 0 3 1.3 3 3v7.5c0 2 1.5 3.5 3.5 3.5 1.5 0 3-1 3.5-3l3.2-18.5C47.8 30 49 26.5 49 22 49 14 41.5 6 32 6z" />
            </svg>
          </div>

          <h2
            className="text-4xl font-bold text-white leading-tight"
            style={{ fontFamily: 'Manrope, system-ui, sans-serif' }}
          >
            Gestion dentaire,<br />
            <span style={{ color: 'var(--teal-400)' }}>simplifiée.</span>
          </h2>
          <p className="text-base leading-relaxed" style={{ color: 'rgba(255,255,255,0.55)' }}>
            Gestion des  patients, consultations, facturations et stocks depuis une seule plateforme.
          </p>

          {/* Features */}
          <div className="space-y-3 pt-2">
            {[
              'Dossiers patients centralisés',
              'Facturation & assurances',
              'Prise de RDV WhatsApp',
              'Gestion du stock et des dépenses'
            ].map((feat) => (
              <div key={feat} className="flex items-center gap-3">
                <div
                  className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0"
                  style={{ background: 'var(--grad-teal)' }}
                >
                  <svg className="w-2.5 h-2.5 text-white fill-current" viewBox="0 0 10 10">
                    <path d="M8.5 2.5L4 7.5 1.5 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="none" />
                  </svg>
                </div>
                <span className="text-sm" style={{ color: 'rgba(255,255,255,0.65)' }}>{feat}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Footer */}
        <p className="relative z-10 text-xs" style={{ color: 'rgba(255,255,255,0.25)' }}>
          © {new Date().getFullYear()} Cabinet Dentaire Sonrisa · Conakry, Guinée
        </p>
      </div>

      {/* ── Right panel — login form ── */}
      <div className="flex-1 flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-sm space-y-8 animate-slide-up">

          {/* Mobile logo */}
          <div className="lg:hidden flex justify-center">
            <img
              src="/Logo - SONRISA.png"
              alt="Sonrisa"
              className="h-14 w-auto"
              style={{ objectFit: 'contain' }}
            />
          </div>

          {/* Heading */}
          <div>
            <h1
              className="text-2xl font-bold text-gray-900"
              style={{ fontFamily: 'Manrope, system-ui, sans-serif' }}
            >
              Connexion
            </h1>
            <p className="mt-1 text-sm text-gray-500">
              Entrez vos identifiants pour accéder à votre espace.
            </p>
          </div>

          {/* Error banner */}
          {error && (
            <div className="alert-error flex items-start gap-2.5 animate-fade-in">
              <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}

          {/* Form */}
          <form className="space-y-5" onSubmit={handleSubmit(onSubmit)} noValidate>
            {/* Username */}
            <div>
              <label htmlFor="username" className="form-label">
                Nom d'utilisateur
              </label>
              <input
                id="username"
                {...register('username', { required: 'Le nom d\'utilisateur est requis' })}
                type="text"
                autoComplete="username"
                className={`form-input ${errors.username ? 'border-red-400' : ''}`}
                placeholder="Votre identifiant"
              />
              {errors.username && (
                <p className="form-error">
                  <AlertCircle className="h-3 w-3" />
                  {errors.username.message}
                </p>
              )}
            </div>

            {/* Password */}
            <div>
              <label htmlFor="password" className="form-label">
                Mot de passe
              </label>
              <div className="relative">
                <input
                  id="password"
                  {...register('password', { required: 'Le mot de passe est requis' })}
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  className={`form-input pr-11 ${errors.password ? 'border-red-400' : ''}`}
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 pr-3.5 flex items-center text-gray-400 hover:text-gray-600 transition-colors"
                  aria-label={showPassword ? 'Masquer' : 'Afficher'}
                >
                  {showPassword
                    ? <EyeOff className="h-4 w-4" />
                    : <Eye className="h-4 w-4" />
                  }
                </button>
              </div>
              {errors.password && (
                <p className="form-error">
                  <AlertCircle className="h-3 w-3" />
                  {errors.password.message}
                </p>
              )}
            </div>

            {/* Submit */}
            <button
              type="submit"
              disabled={isLoading}
              className="btn-primary w-full py-2.5 text-sm mt-2"
              style={{ borderRadius: '12px' }}
            >
              {isLoading ? (
                <>
                  <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                  </svg>
                  Connexion en cours…
                </>
              ) : (
                <>
                  <LogIn className="h-4 w-4" />
                  Se connecter
                </>
              )}
            </button>
          </form>

          {/* Footer */}
          <p className="text-center text-xs text-gray-400">
            Cabinet Dentaire Sonrisa · Conakry, Guinée
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;
